import axios from 'axios';
import type { FormValues } from '../types';
import { templates } from '../data/templates';
import type { HistoryRecord, HistoryStatus, PipefyStatus } from '../../history/data/mockHistory';

const api = axios;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-access-token'] = token;
  }
  return config;
});

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  id?: string | number;
  affectedRows?: number;
}

type BriefingPromocaoRow = Record<string, any>;

export interface BriefingProductValidationItem {
  ean: string;
  sku?: string;
  nomeProduto: string;
  nomeIndustria?: string;
  nomeMarca?: string;
  categoria?: string;
  grupo?: string;
  subGrupo?: string;
  foto?: string;
}

export interface BriefingProductValidationResult {
  requested: string[];
  found: BriefingProductValidationItem[];
  missing: string[];
}

export interface BriefingCnpjGroupValidationResult {
  requested: string[];
  found: string[];
  missing: string[];
}

export interface BriefingUnileverSellersResult {
  sellerIds: string[];
  sellersIncluidos: string[];
  includeEub: boolean;
}

const productValidationCache = new Map<string, BriefingProductValidationItem | null>();
const productValidationInFlight = new Map<string, Promise<BriefingProductValidationResult>>();

function normalizeProductValidationCode(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

function normalizeUniqueProductValidationItems(items: string[]): Array<{ original: string; normalized: string }> {
  const uniqueItems: Array<{ original: string; normalized: string }> = [];
  const seen = new Set<string>();

  items.forEach((item) => {
    const original = String(item ?? '').trim();
    const normalized = normalizeProductValidationCode(original);

    if (!original || !normalized || seen.has(normalized)) return;

    seen.add(normalized);
    uniqueItems.push({ original, normalized });
  });

  return uniqueItems;
}

const LIST_FIELDS = new Set([
  'sellersIncluidos',
  'sellersExcecao',
  'cnpjsIncluidos',
  'cnpjsExcecao',
  'canais',
  'tipoPedido',
  'eansPrioritariosComunicacao',
  'itensIncluidos',
  'itensExcluidos',
  'eansInclusao',
  'eansExclusao',
  'skusInclusao',
  'skusExclusao',
  'marcasInclusao',
  'marcasExclusao',
  'categoriasInclusao',
  'categoriasExclusao',
  'subcategoriasInclusao',
  'subcategoriasExclusao',
  'departamentosInclusao',
  'departamentosExclusao',
  'industriasInclusao',
  'industriasExclusao',
  'familiasInclusao',
  'familiasExclusao',
  'eansObrigatorios',
  'skusObrigatorios',
]);

const CHILD_COUNT_KEY = 'qtdCardsFilhos';
const DISCOUNT_RANGE_COUNT_KEY = 'qtdFaixasDesconto';
const CHILD_FIELD_SUFFIX_RE = /__child(\d+)$/;
const CHILD_MAX_COUNT = 15;
const DISCOUNT_RANGE_MAX_COUNT = 15;
const CHILD_CARD_TEMPLATE_IDS = new Set(['t3', 't4a', 't4b']);
const DISCOUNT_RANGE_TEMPLATE_ID = 't2';
const DISCOUNT_RANGE_SPECIFIC_KEYS = new Set([
  'numeroCard',
  'valorDesconto',
  'criterioDesconto',
  'qtdMinima',
  'qtdMaxima',
  'valorMinimo',
  'valorMaximo',
]);
const CHILD_SPECIFIC_KEYS = new Set([
  'numeroCard',
  'tipoDesconto',
  'valorDesconto',
  'criterioDesconto',
  'qtdMinima',
  'qtdMaxima',
  'valorMinimo',
  'valorMaximo',
  'tipoItemIncluido',
  'itensIncluidos',
  'tipoItemExcluido',
  'itensExcluidos',
  'eansInclusao',
  'eansExclusao',
  'skusInclusao',
  'skusExclusao',
  'marcasInclusao',
  'marcasExclusao',
  'categoriasInclusao',
  'categoriasExclusao',
  'subcategoriasInclusao',
  'subcategoriasExclusao',
  'departamentosInclusao',
  'departamentosExclusao',
  'industriasInclusao',
  'industriasExclusao',
  'familiasInclusao',
  'familiasExclusao',
  'eansObrigatorios',
  'skusObrigatorios',
  'skuCombo',
  'tipoFamilia',
  'nomeFamilia',
  'industriaFamilia',
  'obsFamilia',
  'skusFamilia',
  'qtdEANsDistintos',
  'qtdEansDistintos',
  'qtdMinimaAtivacao',
  'qtdMinAtivacao',
  'qtdMaximaAtivacao',
  'qtdMaxAtivacao',
  'qtdFamiliasAtivacao',
  'familiaObrigatoria',
  'posicaoFamilia',
  'posicaoFamiliaLista',
  'descontoFamilia',
  'posicaoLista',
  'posicaoListaSecao',
  'secaoLista',
]);

const COLUMN_TO_FORM_KEY: Record<string, string> = {
  briefing_group_id: 'briefingGroupId',
  parent_briefing_id: 'parentBriefingId',
  template_id: 'templateId',
  tipo_solicitacao: 'tipoSolicitacao',
  id_ifc: 'idIFC',
  tipo_mecanica: 'tipoMecanica',
  tipo_lista: 'tipoLista',
  recompra_disponivel: 'recompraDisponivel',
  id_lista_original: 'idListaOriginal',
  nome_promo: 'nomePromo',
  nome_exibicao: 'nomeExibicao',
  descricao: 'descricao',
  descricao_interna: 'descricaoInterna',
  tipo_desconto: 'tipoDesconto',
  valor_desconto: 'valorDesconto',
  industria: 'industria',
  multi_patrocinador: 'multiPatrocinador',
  patrocinadores: 'patrocinadores',
  cumulativa: 'cumulativa',
  inicio_vigencia: 'inicioVigencia',
  fim_vigencia: 'fimVigencia',
  promocao_prioritaria_comunicacao: 'promocaoPrioritariaComunicacao',
  eans_prioritarios_comunicacao: 'eansPrioritariosComunicacao',
  briefing_comunicacao: 'briefingComunicacao',
  budget_atrelado: 'budgetAtrelado',
  budget_valor: 'budgetValor',
  sellers_incluidos: 'sellersIncluidos',
  sellers_excecao: 'sellersExcecao',
  cnpjs_incluidos: 'cnpjsIncluidos',
  cnpjs_excecao: 'cnpjsExcecao',
  canais: 'canais',
  tipo_pedido: 'tipoPedido',
  criterio_desconto: 'criterioDesconto',
  qtd_minima: 'qtdMinima',
  qtd_maxima: 'qtdMaxima',
  valor_minimo: 'valorMinimo',
  valor_maximo: 'valorMaximo',
  numero_card: 'numeroCard',
  tipo_item_incluido: 'tipoItemIncluido',
  itens_incluidos: 'itensIncluidos',
  tipo_item_excluido: 'tipoItemExcluido',
  itens_excluidos: 'itensExcluidos',
  eans_inclusao: 'eansInclusao',
  eans_exclusao: 'eansExclusao',
  skus_inclusao: 'skusInclusao',
  skus_exclusao: 'skusExclusao',
  marcas_inclusao: 'marcasInclusao',
  marcas_exclusao: 'marcasExclusao',
  categorias_inclusao: 'categoriasInclusao',
  categorias_exclusao: 'categoriasExclusao',
  subcategorias_inclusao: 'subcategoriasInclusao',
  subcategorias_exclusao: 'subcategoriasExclusao',
  departamentos_inclusao: 'departamentosInclusao',
  departamentos_exclusao: 'departamentosExclusao',
  industrias_inclusao: 'industriasInclusao',
  industrias_exclusao: 'industriasExclusao',
  familias_inclusao: 'familiasInclusao',
  familias_exclusao: 'familiasExclusao',
  eans_obrigatorios: 'eansObrigatorios',
  skus_obrigatorios: 'skusObrigatorios',
  sku_combo: 'skuCombo',
  tipo_familia: 'tipoFamilia',
  nome_familia: 'nomeFamilia',
  industria_familia: 'industriaFamilia',
  obs_familia_existente: 'obsFamilia',
  skus_familia: 'skusFamilia',
  qtd_eans_distintos: 'qtdEANsDistintos',
  qtd_min_ativacao: 'qtdMinimaAtivacao',
  qtd_max_ativacao: 'qtdMaximaAtivacao',
  qtd_familias_ativacao: 'qtdFamiliasAtivacao',
  familia_obrigatoria: 'familiaObrigatoria',
  posicao_familia_lista: 'posicaoFamilia',
  desconto_familia: 'descontoFamilia',
  posicao_lista_secao: 'posicaoLista',
  secao_lista: 'secaoLista',
  cupom: 'cupom',
  codigo_cupom: 'codigoCupom',
  qtd_cupons_disponiveis: 'qtdCupons',
  qtd_usos_por_cliente: 'usosPorCliente',
};

function handleResponse<T>(res: { data: ApiResponse<T> }): T {
  if (!res.data.success) throw new Error(res.data.error || 'API Error');
  return res.data.data;
}

function handleMutationResponse<T = any>(res: { data: ApiResponse<T> }): ApiResponse<T> {
  if (!res.data.success) throw new Error(res.data.error || 'API Error');
  return res.data;
}

function splitList(value: any): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value === null || value === undefined || value === '') return [];

  const str = String(value).trim();
  if (!str) return [];

  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch (_) {
    // O backend atual grava listas separadas por vírgula.
  }

  return str
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatDateTimeToForm(value: any): string {
  if (!value) return '';

  if (typeof value === 'string') {
    const brMatch = value.match(/^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}/);
    if (brMatch) return value.slice(0, 16);

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (isoMatch) {
      const [, yyyy, mm, dd, hh, min] = isoMatch;
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formDateToIsoLike(value: any): string {
  if (!value) return '';

  if (typeof value === 'string') {
    const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
    if (brMatch) {
      const [, dd, mm, yyyy, hh, min] = brMatch;
      return `${yyyy}-${mm}-${dd}T${hh}:${min}:00`;
    }

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (isoMatch) return value.replace(' ', 'T');
  }

  return String(value);
}

function normalizeScalar(value: any): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function normalizeMandatoryFamilyValue(value: any): string {
  const normalized = normalizeScalar(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (['1', 'sim', 's', 'yes', 'y', 'true'].includes(normalized)) return 'SIM';
  if (['0', 'nao', 'n', 'no', 'false'].includes(normalized)) return 'NÃO';

  return normalizeScalar(value);
}

function normalizeRepurchaseAvailableValue(value: any): string {
  const normalized = normalizeScalar(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (['1', 'yes', 'sim', 's', 'true'].includes(normalized)) return 'Yes';
  if (['0', 'no', 'nao', 'n', 'false'].includes(normalized)) return 'No';

  return normalizeScalar(value);
}

function normalizeChildCount(value: unknown): number {
  if (Array.isArray(value)) return 0;
  const parsed = parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(CHILD_MAX_COUNT, parsed));
}

function normalizeDiscountRangeCount(value: unknown): number {
  if (Array.isArray(value)) return 0;
  const parsed = parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(1, Math.min(DISCOUNT_RANGE_MAX_COUNT, parsed));
}

function createBriefingGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `briefing-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getChildFieldInfo(key: string): { baseKey: string; childNumber: number } | null {
  const match = key.match(CHILD_FIELD_SUFFIX_RE);
  if (!match) return null;

  const childNumber = parseInt(match[1], 10);
  if (Number.isNaN(childNumber)) return null;

  return {
    baseKey: key.replace(CHILD_FIELD_SUFFIX_RE, ''),
    childNumber,
  };
}

function buildCardPayloads(templateId: string, values: FormValues): FormValues[] {
  const parentPayload: FormValues = {};

  Object.entries(values).forEach(([key, value]) => {
    if (key === CHILD_COUNT_KEY || key === DISCOUNT_RANGE_COUNT_KEY || getChildFieldInfo(key)) return;
    parentPayload[key] = value;
  });

  if (templateId === DISCOUNT_RANGE_TEMPLATE_ID) {
    const rangeCount = parentPayload.numeroCard === '1'
      ? normalizeDiscountRangeCount(values[DISCOUNT_RANGE_COUNT_KEY])
      : 0;

    if (rangeCount <= 1) return [parentPayload];

    const rangePayloads: FormValues[] = [];

    for (let rangeNumber = 2; rangeNumber <= rangeCount; rangeNumber += 1) {
      const rangePayload: FormValues = {
        ...parentPayload,
        numeroCard: String(rangeNumber),
      };

      DISCOUNT_RANGE_SPECIFIC_KEYS.forEach((key) => {
        if (key !== 'numeroCard') delete rangePayload[key];
      });

      Object.entries(values).forEach(([key, value]) => {
        const childInfo = getChildFieldInfo(key);
        if (!childInfo || childInfo.childNumber !== rangeNumber) return;
        if (!DISCOUNT_RANGE_SPECIFIC_KEYS.has(childInfo.baseKey)) return;
        rangePayload[childInfo.baseKey] = value;
      });

      rangePayload.numeroCard = String(rangeNumber);
      rangePayloads.push(rangePayload);
    }

    return [parentPayload, ...rangePayloads];
  }

  const childCount = CHILD_CARD_TEMPLATE_IDS.has(templateId) && parentPayload.numeroCard === '1'
    ? normalizeChildCount(values[CHILD_COUNT_KEY])
    : 0;

  if (childCount <= 0) return [parentPayload];

  const childPayloads: FormValues[] = [];

  for (let childNumber = 2; childNumber <= childCount + 1; childNumber += 1) {
    const childPayload: FormValues = {
      ...parentPayload,
      numeroCard: String(childNumber),
    };

    CHILD_SPECIFIC_KEYS.forEach((key) => {
      if (key !== 'numeroCard') delete childPayload[key];
    });

    Object.entries(values).forEach(([key, value]) => {
      const childInfo = getChildFieldInfo(key);
      if (!childInfo || childInfo.childNumber !== childNumber) return;
      childPayload[childInfo.baseKey] = value;
    });

    childPayload.numeroCard = String(childNumber);
    childPayloads.push(childPayload);
  }

  return [parentPayload, ...childPayloads];
}

async function postBriefingPromocao(templateId: string, values: FormValues): Promise<string | number | undefined> {
  const res = await api.post('/briefing-promocoes', {
    ...values,
    templateId,
  });
  return handleMutationResponse(res).id;
}

function mapBriefingRowToValues(row: BriefingPromocaoRow): FormValues {
  const values: FormValues = {};

  Object.entries(COLUMN_TO_FORM_KEY).forEach(([column, key]) => {
    const raw = row[column];
    if (raw === undefined || raw === null) return;

    if (key === 'inicioVigencia' || key === 'fimVigencia') {
      values[key] = formatDateTimeToForm(raw);
    } else if (LIST_FIELDS.has(key)) {
      values[key] = splitList(raw);
    } else if (key === 'familiaObrigatoria') {
      values[key] = normalizeMandatoryFamilyValue(raw);
    } else if (key === 'recompraDisponivel') {
      values[key] = normalizeRepurchaseAvailableValue(raw);
    } else {
      values[key] = normalizeScalar(raw);
    }
  });

  return values;
}

function childFieldKey(fieldKey: string, childNumber: number): string {
  return `${fieldKey}__child${childNumber}`;
}

function mergeChildRowsIntoValues(parentValues: FormValues, childValuesList: FormValues[]): FormValues {
  if (!childValuesList.length) return parentValues;

  const values: FormValues = { ...parentValues };
  const childNumbers: number[] = [];

  childValuesList.forEach((childValues) => {
    const childNumber = parseInt(String(childValues.numeroCard ?? '').replace(/[^0-9]/g, ''), 10);
    if (Number.isNaN(childNumber) || childNumber < 2) return;

    childNumbers.push(childNumber);
    values[childFieldKey('numeroCard', childNumber)] = String(childNumber);

    Object.entries(childValues).forEach(([key, value]) => {
      if (key === 'numeroCard') return;
      if (key === 'templateId' || key === 'briefingGroupId' || key === 'parentBriefingId') return;
      values[childFieldKey(key, childNumber)] = value;
    });
  });

  if (childNumbers.length) {
    values[CHILD_COUNT_KEY] = String(Math.min(CHILD_MAX_COUNT, Math.max(...childNumbers) - 1));
  }

  return values;
}

function mergeDiscountRangeRowsIntoValues(parentValues: FormValues, childValuesList: FormValues[]): FormValues {
  if (!childValuesList.length) return parentValues;

  const values: FormValues = { ...parentValues };
  const rangeNumbers: number[] = [];

  childValuesList.forEach((childValues) => {
    const rangeNumber = parseInt(String(childValues.numeroCard ?? '').replace(/[^0-9]/g, ''), 10);
    if (Number.isNaN(rangeNumber) || rangeNumber < 2) return;

    rangeNumbers.push(rangeNumber);

    Object.entries(childValues).forEach(([key, value]) => {
      if (!DISCOUNT_RANGE_SPECIFIC_KEYS.has(key)) return;
      if (key === 'numeroCard') return;
      values[childFieldKey(key, rangeNumber)] = value;
    });
  });

  if (rangeNumbers.length) {
    values[DISCOUNT_RANGE_COUNT_KEY] = String(Math.min(DISCOUNT_RANGE_MAX_COUNT, Math.max(...rangeNumbers)));
  }

  return values;
}

function normalizeActivationMaxQuantityValues(templateId: string, values: FormValues): FormValues {
  if (!CHILD_CARD_TEMPLATE_IDS.has(templateId)) return values;

  const normalizedValues: FormValues = { ...values };

  Object.keys(normalizedValues).forEach((key) => {
    const childInfo = getChildFieldInfo(key);
    const baseKey = childInfo ? childInfo.baseKey : key;

    if (baseKey !== 'qtdMaximaAtivacao' && baseKey !== 'qtdMaxAtivacao') return;

    const normalized = String(normalizedValues[key] ?? '').trim();
    if (normalized === '99999' || normalized === '999999') {
      normalizedValues[key] = '';
    }
  });

  return normalizedValues;
}

function normalizeCouponQuantityValues(values: FormValues): FormValues {
  const normalizedValues: FormValues = { ...values };

  Object.keys(normalizedValues).forEach((key) => {
    const childInfo = getChildFieldInfo(key);
    const baseKey = childInfo ? childInfo.baseKey : key;

    if (baseKey !== 'qtdCupons' && baseKey !== 'qtdCuponsDisponiveis') return;

    const normalized = String(normalizedValues[key] ?? '').trim();
    if (normalized === '99999' || normalized === '999999') {
      normalizedValues[key] = '';
    }
  });

  return normalizedValues;
}

function getTemplateId(row: BriefingPromocaoRow, values: FormValues): string {
  const explicit = (values.templateId as string) || row.template_id;
  if (explicit && templates.some((template) => template.id === explicit)) return explicit;

  const tipoMecanica = String(values.tipoMecanica || row.tipo_mecanica || '');
  const tipoLista = String(values.tipoLista || row.tipo_lista || '');
  const cupom = String(values.cupom || row.cupom || '');

  if (tipoMecanica === '2') return 't2';
  if (tipoMecanica === '3') return 't3';
  if (tipoMecanica === '4') return tipoLista === '2' ? 't4b' : 't4a';
  if (tipoMecanica === '5') return 't5';
  if (tipoMecanica === '6') return 't6';
  if (tipoMecanica === '7') return 't7';
  if (cupom === '2') return 't8';

  return 't1';
}

function getStatus(values: FormValues): HistoryStatus {
  const startValue = formDateToIsoLike(values.inicioVigencia);
  const endValue = formDateToIsoLike(values.fimVigencia);

  if (!startValue || !endValue) return 'rascunho';

  const now = new Date();
  const start = new Date(startValue);
  const end = new Date(endValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'rascunho';
  if (now < start) return 'agendado';
  if (now > end) return 'encerrado';

  return 'ativo';
}

export function mapBriefingRowToHistoryRecord(row: BriefingPromocaoRow): HistoryRecord {
  let values = mapBriefingRowToValues(row);
  const templateId = getTemplateId(row, values);

  if (templateId === DISCOUNT_RANGE_TEMPLATE_ID && Array.isArray(row.children)) {
    values = mergeDiscountRangeRowsIntoValues(values, row.children.map(mapBriefingRowToValues));
  } else if (CHILD_CARD_TEMPLATE_IDS.has(templateId) && Array.isArray(row.children)) {
    values = mergeChildRowsIntoValues(values, row.children.map(mapBriefingRowToValues));
  }

  if (templateId === DISCOUNT_RANGE_TEMPLATE_ID && values.numeroCard === '1' && !values[DISCOUNT_RANGE_COUNT_KEY]) {
    values[DISCOUNT_RANGE_COUNT_KEY] = '1';
  }

  values = normalizeCouponQuantityValues(normalizeActivationMaxQuantityValues(templateId, values));
  values.templateId = templateId;

  const template = templates.find((item) => item.id === templateId) || templates[0];

  return {
    id: String(row.id),
    briefingGroupId: row.briefing_group_id ? String(row.briefing_group_id) : undefined,
    parentBriefingId: row.parent_briefing_id ? String(row.parent_briefing_id) : undefined,
    templateId,
    templateName: template?.name || 'Template não identificado',
    templateColor: template?.color || '#1976d2',
    templateNumber: template?.number || '—',
    createdAt: row.criado_em || row.created_at || '',
    updatedAt: row.atualizado_em || row.updated_at || row.criado_em || '',
    status: getStatus(values),
    author: row.criado_por_nome || row.atualizado_por_nome || '—',
    pipefyStatus: (row.pipefy_status || 'not_sent') as PipefyStatus,
    pipefyStatusSource: row.pipefy_status_source || 'system',
    pipefyJobId: row.pipefy_job_guid || undefined,
    pipefyImportId: row.pipefy_import_id || undefined,
    pipefySentAt: row.pipefy_sent_at || undefined,
    pipefyStatusUpdatedAt: row.pipefy_status_updated_at || undefined,
    pipefyStatusUpdatedBy: row.pipefy_status_updated_by || undefined,
    pipefyLastError: row.pipefy_last_error || undefined,
    values,
  };
}

export async function fetchBriefingPromocoes(params?: Record<string, string | number | undefined>): Promise<HistoryRecord[]> {
  const res = await api.get('/briefing-promocoes', { params });
  const rows = handleResponse<BriefingPromocaoRow[]>(res);
  return rows.map(mapBriefingRowToHistoryRecord);
}

export async function fetchBriefingPromocao(id: string | number): Promise<HistoryRecord | null> {
  const res = await api.get(`/briefing-promocoes/${id}`);
  const row = handleResponse<BriefingPromocaoRow | null>(res);
  return row ? mapBriefingRowToHistoryRecord(row) : null;
}

export async function createBriefingPromocao(templateId: string, values: FormValues): Promise<string | number | undefined> {
  const payloads = buildCardPayloads(templateId, normalizeCouponQuantityValues(values));
  const briefingGroupId = payloads.length > 1 ? createBriefingGroupId() : undefined;
  let parentId: string | number | undefined;

  for (let index = 0; index < payloads.length; index += 1) {
    const payload = { ...payloads[index] };

    if (briefingGroupId) {
      payload.briefingGroupId = briefingGroupId;
    }

    if (index > 0) {
      if (parentId === undefined || parentId === null || parentId === '') {
        throw new Error('Não foi possível vincular os cards filhos ao briefing pai.');
      }

      payload.parentBriefingId = String(parentId);
    }

    const id = await postBriefingPromocao(templateId, payload);
    if (parentId === undefined) parentId = id;
  }

  return parentId;
}

export async function updateBriefingPromocao(id: string | number, templateId: string, values: FormValues): Promise<boolean> {
  const res = await api.put(`/briefing-promocoes/${id}`, {
    ...normalizeCouponQuantityValues(values),
    templateId,
  });
  handleMutationResponse(res);
  return true;
}

export async function deleteBriefingPromocao(id: string | number): Promise<boolean> {
  const res = await api.delete(`/briefing-promocoes/${id}`);
  handleMutationResponse(res);
  return true;
}

export interface BriefingPromotionImportTotals {
  promotions: number;
  cards: number;
  parents: number;
  children: number;
  standalone: number;
}

export interface BriefingPromotionImportCardPreview {
  id: string;
  sheetName: string;
  rowNumber: number;
  templateId: string | null;
  templateName: string;
  cardNumber: number | null;
  role: 'parent' | 'child' | 'single';
  values: FormValues;
  errors: string[];
  warnings: string[];
}

export interface BriefingPromotionImportGroupPreview {
  id: string;
  sheetName: string;
  templateId: string | null;
  templateName: string;
  name: string;
  cards: BriefingPromotionImportCardPreview[];
  errors: string[];
  warnings: string[];
}

export interface BriefingPromotionImportPreview {
  fileName: string;
  checksum: string;
  canConfirm: boolean;
  sheets: Array<{
    name: string;
    headerRow: number;
    rowCount: number;
    recognizedColumns: number;
  }>;
  totals: BriefingPromotionImportTotals;
  templates: Array<{
    templateId: string;
    templateName: string;
    cardCount: number;
    promotionCount: number;
  }>;
  groups: BriefingPromotionImportGroupPreview[];
  errors: string[];
  warnings: string[];
}

export interface BriefingPromotionImportResult {
  checksum: string;
  totals: BriefingPromotionImportTotals;
  promotions: Array<{
    templateId: string;
    name: string;
    parentId: string | number;
    cardIds: Array<string | number>;
  }>;
}

function getImportError(error: unknown, fallback: string): Error {
  if (axios.isAxiosError(error)) {
    const response = error.response?.data as {
      error?: string;
      details?: string[];
      data?: BriefingPromotionImportPreview;
    } | undefined;
    const message = response?.details?.[0] || response?.error || error.message || fallback;
    const importError = new Error(message) as Error & { preview?: BriefingPromotionImportPreview };
    importError.preview = response?.data;
    return importError;
  }

  return error instanceof Error ? error : new Error(fallback);
}

export async function previewBriefingPromotionsImport(file: File): Promise<BriefingPromotionImportPreview> {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const res = await api.post<ApiResponse<BriefingPromotionImportPreview>>(
      '/briefing-promocoes/import/preview',
      formData
    );
    return handleResponse(res);
  } catch (error) {
    throw getImportError(error, 'Não foi possível analisar o arquivo de promoções.');
  }
}

export async function confirmBriefingPromotionsImport(
  file: File,
  checksum: string
): Promise<BriefingPromotionImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('checksum', checksum);

  try {
    const res = await api.post<ApiResponse<BriefingPromotionImportResult>>(
      '/briefing-promocoes/import/confirm',
      formData
    );
    return handleResponse(res);
  } catch (error) {
    throw getImportError(error, 'Não foi possível cadastrar as promoções.');
  }
}
export async function validateBriefingProducts(items: string[]): Promise<BriefingProductValidationResult> {
  const uniqueItems = normalizeUniqueProductValidationItems(items);

  if (uniqueItems.length === 0) {
    return {
      requested: [],
      found: [],
      missing: [],
    };
  }

  const uncachedItems = uniqueItems.filter((item) => !productValidationCache.has(item.normalized));

  if (uncachedItems.length > 0) {
    const requestKey = uncachedItems
      .map((item) => item.normalized)
      .sort()
      .join('\n');

    let request = productValidationInFlight.get(requestKey);

    if (!request) {
      request = api.post('/briefing-promocoes/produtos/validar', {
        items: uncachedItems.map((item) => item.original),
      }).then((res) => handleResponse<BriefingProductValidationResult>(res));

      productValidationInFlight.set(requestKey, request);
      request.finally(() => productValidationInFlight.delete(requestKey));
    }

    const fetched = await request;
    const fetchedByCode = new Map<string, BriefingProductValidationItem>();

    fetched.found.forEach((product) => {
      [product.ean, product.sku].forEach((code) => {
        const normalized = normalizeProductValidationCode(code);
        if (normalized) fetchedByCode.set(normalized, product);
      });
    });

    uncachedItems.forEach((item) => {
      productValidationCache.set(item.normalized, fetchedByCode.get(item.normalized) || null);
    });
  }

  const found: BriefingProductValidationItem[] = [];
  const missing: string[] = [];
  const emittedFoundCodes = new Set<string>();

  uniqueItems.forEach((item) => {
    const cachedProduct = productValidationCache.get(item.normalized);

    if (!cachedProduct) {
      missing.push(item.original);
      return;
    }

    if (emittedFoundCodes.has(item.normalized)) return;
    emittedFoundCodes.add(item.normalized);
    found.push(cachedProduct);
  });

  return {
    requested: uniqueItems.map((item) => item.original),
    found,
    missing,
  };
}

export async function validateBriefingCnpjGroups(groups: string[]): Promise<BriefingCnpjGroupValidationResult> {
  const uniqueGroups = Array.from(
    new Set(groups.map((group) => String(group ?? '').trim()).filter(Boolean))
  );

  if (uniqueGroups.length === 0) {
    return {
      requested: [],
      found: [],
      missing: [],
    };
  }

  const res = await api.post('/briefing-promocoes/cnpj-grupos/validar', {
    groups: uniqueGroups,
  });

  return handleResponse<BriefingCnpjGroupValidationResult>(res);
}

export async function getBriefingUnileverSellers(includeEub = false): Promise<BriefingUnileverSellersResult> {
  const res = await api.get('/briefing-promocoes-agent/unilever-sellers', {
    params: {
      includeEub: includeEub ? '1' : undefined,
    },
  });

  return handleResponse<BriefingUnileverSellersResult>(res);
}


export interface BriefingPromocoesAgentResponse {
  message: string;
  formValues: FormValues;
  fields: FormValues;
  templateId: string | null;
  missingFields: string[];
  shouldFill: boolean;
}

export async function sendBriefingPromocoesAgentMessage(params: {
  chatId: string;
  message: string;
  templateId: string;
  templateName?: string;
  currentValues: FormValues;
}): Promise<BriefingPromocoesAgentResponse> {
  const res = await api.post<ApiResponse<BriefingPromocoesAgentResponse>>(
    '/briefing-promocoes-agent/chat',
    params
  );

  if (!res.data.success) {
    throw new Error(res.data.error || 'Erro ao conversar com o agente.');
  }

  return res.data.data;
}
export interface BriefingPipefyImportResult {
  jobId: string;
  status: string;
  state: string;
  templateId: string;
  templateName: string;
  rowCount: number;
  columnCount: number;
}

export async function enqueueBriefingsPipefyImport(params: {
  templateId: string;
  briefingIds: Array<string | number>;
  file: File;
}): Promise<BriefingPipefyImportResult> {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('templateId', params.templateId);
  formData.append('briefingIds', JSON.stringify(params.briefingIds.map(String)));

  const res = await api.post<ApiResponse<BriefingPipefyImportResult>>(
    '/briefing-promocoes/pipefy/import',
    formData
  );
  return handleResponse(res);
}

export interface BriefingPipefyCancelResult {
  briefingIds: string[];
  phase: { id: string; name: string; done: boolean };
  cardCount: number;
  movedCards: number;
  alreadyCancelledCards: number;
  jobs: Array<{
    jobId: string;
    briefingIds: string[];
    cardCount: number;
    movedCards: number;
    alreadyCancelledCards: number;
    failedCards?: number;
  }>;
}

export async function cancelBriefingsPipefy(
  briefingIds: Array<string | number>,
): Promise<BriefingPipefyCancelResult> {
  const res = await api.post<ApiResponse<BriefingPipefyCancelResult>>(
    '/briefing-promocoes/pipefy/cancel',
    { briefingIds: briefingIds.map(String) },
  );
  return handleResponse(res);
}

export interface PipefyCardSummary {
  id: string;
  title: string;
  createdAt?: string | null;
  currentPhase?: { id: string; name: string } | null;
}

export interface PipefyCardsCancelByIdsResult {
  requestedCount: number;
  destinationPhase: { id: string; name: string; done: boolean };
  moved: PipefyCardSummary[];
  alreadyCancelled: PipefyCardSummary[];
  failed: Array<{ cardId: string; error: string }>;
}

export async function cancelPipefyCardsByIds(
  cardIds: Array<string | number>,
): Promise<PipefyCardsCancelByIdsResult> {
  const res = await api.post<ApiResponse<PipefyCardsCancelByIdsResult>>(
    '/briefing-promocoes/pipefy/cards/cancel',
    { cardIds: cardIds.map(String) },
  );
  return handleResponse(res);
}
